import { useToast, useAppVariant, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useShowAlert } from "@jugl-web/domain-resources/ui-components";
import { environment } from "@web-src/environments/environment";
import {
  getIntegrationTileState,
  groupConnectionsByType,
} from "@web-src/modules/cx/pages/CXAIAgentsPage/utils";
import capitalize from "lodash/capitalize";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { buildShopifyAuthorizeUrl } from "@web-src/modules/cx/utils/shopifyOAuth";
import {
  CXIntegrationTileProps,
  CXIntegrationTile,
} from "./components/CXIntegrationTile";
import { ReactComponent as SquareIcon } from "./assets/square.svg";
import { ReactComponent as ShopifyIcon } from "./assets/shopify.svg";
import { ReactComponent as CalendlyIcon } from "./assets/calendly.svg";
import { ReactComponent as CalcomIcon } from "./assets/calcom.svg";
import { ConnectionPickerDialog } from "./components/ConnectionPickerDialog";
import {
  IntegrationsHowItWorksDialog,
  IntegrationHowItWorksKind,
} from "./components/IntegrationsHowItWorksDialog";
import { ShopifyStorePromptDialog } from "./components/ShopifyStorePromptDialog";

// Shopify uses the public-app OAuth flow: prompt for the store domain → redirect
// to Shopify authorize → /connections/shopify callback.

type OAuthProvider = "calendly" | "squareup" | "cal";

// Packs disconnectable from this section. Calendly/Square/Cal.com share the
// OAuth flow; Shopify uses its own connect path but the disconnect path is the
// same. Kept narrower than `AgentToolPack` so `generic`/`custom` can't reach
// the handler.
type DisconnectablePack = OAuthProvider | "shopify";

type PickerState = {
  provider: OAuthProvider;
  connectionId: string;
};

type PendingConnectionStep = {
  provider: OAuthProvider;
  connectionId: string;
};

type ConnectionError = {
  provider: OAuthProvider | "shopify";
  error: string;
};

const buildOAuthUrl = (
  provider: OAuthProvider,
  { entityId, name }: { entityId: string; name: string }
) => {
  const finalCallbackUrl = `${window.location.origin}/connections/${provider}`;
  const params = new URLSearchParams({
    entity_id: entityId,
    name,
    final_callback_url: finalCallbackUrl,
  });
  return `${
    environment.apiUrl
  }/api/connections/${provider}/authorize?${params.toString()}`;
};

export const IntegrationsSection: React.FC<{ entityId: string }> = ({
  entityId,
}) => {
  const { t } = useTranslations();
  const { variant } = useAppVariant();
  const { toast } = useToast({ variant });
  const showAlert = useShowAlert();
  const { integrationsApi } = useRestApiProvider();
  const [deleteConnection] = integrationsApi.useDeleteConnectionMutation();
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAnonymous } = useAnonymousSession();
  const cxSignupDialog = useCXSignupDialog();

  const { data: allConnections } = integrationsApi.useGetConnectionsQuery({
    entityId,
  });
  const { data: agentConfig } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
  });

  const connectionsByType = useMemo(
    () => groupConnectionsByType(allConnections?.data),
    [allConnections?.data]
  );

  const calendlyTile = useMemo(
    () => getIntegrationTileState(connectionsByType.calendly),
    [connectionsByType]
  );
  const squareupTile = useMemo(
    () => getIntegrationTileState(connectionsByType.squareup),
    [connectionsByType]
  );
  const calcomTile = useMemo(
    () => getIntegrationTileState(connectionsByType.cal),
    [connectionsByType]
  );
  const isShopifyConnected = Boolean(connectionsByType.shopify?.length);

  // Open the connection-setup dialog from router state. Read it lazily (rather
  // than in an effect that immediately clears it) so the dialog survives a page
  // remount — the step stays in `location.state` until the dialog is closed.
  const [picker, setPicker] = useState<PickerState | null>(() => {
    const state = location.state as {
      pendingConnectionStep?: PendingConnectionStep;
    } | null;
    return state?.pendingConnectionStep ?? null;
  });
  const [howItWorksKind, setHowItWorksKind] =
    useState<IntegrationHowItWorksKind | null>(null);
  const [isShopifyStorePromptOpen, setIsShopifyStorePromptOpen] =
    useState(false);

  const redirectToShopifyAuthorize = useCallback(
    (store: string) => {
      window.location.href = buildShopifyAuthorizeUrl(store, {
        state: entityId,
      });
    },
    [entityId]
  );

  // Drop the one-shot `pendingConnectionStep` key but keep `defaultTab` so a
  // later (re)mount of the page still resolves to the Automate tab.
  const closePicker = useCallback(() => {
    setPicker(null);
    const state = location.state as {
      defaultTab?: string;
      pendingConnectionStep?: PendingConnectionStep;
    } | null;
    if (state?.pendingConnectionStep) {
      navigate(location.pathname, {
        replace: true,
        state: state.defaultTab ? { defaultTab: state.defaultTab } : null,
      });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const state = location.state as {
      defaultTab?: string;
      connectionError?: ConnectionError;
    } | null;
    if (state?.connectionError) {
      toast(
        t(
          {
            id: "cx-actions-page.integration-connection-error",
            defaultMessage: "{provider} connection failed",
          },
          {
            provider: capitalize(state.connectionError.provider),
          }
        ),
        { variant: "error" }
      );
      navigate(location.pathname, {
        replace: true,
        state: state.defaultTab ? { defaultTab: state.defaultTab } : null,
      });
    }
  }, [location.state, location.pathname, navigate, toast, t]);

  const guardConnect = useCallback(
    (action: () => void) => {
      if (isAnonymous) {
        cxSignupDialog?.open();
        return;
      }
      action();
    },
    [isAnonymous, cxSignupDialog]
  );

  const startOAuth = useCallback(
    (provider: OAuthProvider, name: string) => {
      window.location.href = buildOAuthUrl(provider, { entityId, name });
    },
    [entityId]
  );

  const openPicker = useCallback(
    (provider: OAuthProvider, connectionId: string | null) => {
      if (!connectionId) return;
      setPicker({ provider, connectionId });
    },
    []
  );

  const handleDisconnect = useCallback(
    (
      integrationName: string,
      pack: DisconnectablePack,
      connectionId: string | null
    ) => {
      if (!connectionId) return;
      showAlert({
        title: t(
          {
            id: "cx-actions-page.disconnect-title",
            defaultMessage: "Disconnect {integration}?",
          },
          { integration: integrationName }
        ),
        content: t({
          id: "cx-actions-page.disconnect-content",
          defaultMessage:
            "This will remove the connection. Any agent tools using this connection will stop working.",
        }),
        buttons: [
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            color: "grey",
            role: "close",
          },
          {
            text: t({
              id: "cx-actions-page.disconnect-confirm",
              defaultMessage: "Disconnect",
            }),
            color: "tertiary",
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              try {
                await deleteConnection({ entityId, connectionId }).unwrap();
                // Drop the agent-config tool entry for this pack. Non-custom
                // packs only ever have a single entry, and `custom` is managed
                // separately by CustomSection — so filtering by pack is both
                // safe and catches stale entries that no longer carry the
                // matching connection_id. Best-effort: the connection is
                // already gone even if this fails.
                if (agentConfig) {
                  const existingTools = agentConfig.config.tools ?? [];
                  const nextTools = existingTools.filter(
                    (tool) => tool.pack !== pack
                  );
                  if (nextTools.length !== existingTools.length) {
                    await updateAgentConfig({
                      entityId,
                      config: { ...agentConfig.config, tools: nextTools },
                    })
                      .unwrap()
                      .catch(() => undefined);
                  }
                }
                toast(
                  t({
                    id: "cx-actions-page.disconnect-success",
                    defaultMessage: "Disconnected successfully",
                  })
                );
              } catch {
                toast(
                  t({
                    id: "cx-actions-page.disconnect-error",
                    defaultMessage: "Failed to disconnect",
                  })
                );
              }
            },
          },
        ],
      });
    },
    [
      showAlert,
      deleteConnection,
      updateAgentConfig,
      agentConfig,
      entityId,
      toast,
      t,
    ]
  );

  const pendingLabel = t({
    id: "cx-actions-page.finish-setup",
    defaultMessage: "Finish setup",
  });
  const pendingTooltip = t({
    id: "cx-actions-page.connection-requires-configuration",
    defaultMessage:
      "Connection created but not yet configured. Finish setup to enable Actions.",
  });
  const shoppingAutomationConnectDisabledTooltip = t({
    id: "cx-actions-page.single-shopping-automation-limit",
    defaultMessage:
      "ℹ️ Only one Shopping Automation can be active at the Workspace at a time",
  });
  const schedulingAutomationConnectDisabledTooltip = t({
    id: "cx-actions-page.single-scheduling-automation-limit",
    defaultMessage:
      "ℹ️ Only one Scheduling Automation can be active at the Workspace at a time",
  });

  const integrations: (CXIntegrationTileProps & { id: string })[] =
    useMemo(() => {
      const baseTile = {
        isConnecting: false,
        isReconnecting: false,
      };
      const isCalendlyActive =
        calendlyTile.isConnected &&
        !calendlyTile.isExpired &&
        !calendlyTile.needsSetup;
      const isCalcomActive =
        calcomTile.isConnected &&
        !calcomTile.isExpired &&
        !calcomTile.needsSetup;
      const isSquareActive =
        squareupTile.isConnected &&
        !squareupTile.isExpired &&
        !squareupTile.needsSetup;
      const isShopifyActive = isShopifyConnected;

      const isShopifyConnectDisabled = !isShopifyActive && isSquareActive;
      const isSquareConnectDisabled = isShopifyActive && !isSquareActive;
      const isCalendlyConnectDisabled = isCalcomActive && !isCalendlyActive;
      const isCalcomConnectDisabled = isCalendlyActive && !isCalcomActive;

      return [
        {
          ...baseTile,
          id: "calendly",
          title: t({
            id: "cx-actions-page.calendly",
            defaultMessage: "Calendly",
          }),
          description: t({
            id: "cx-actions-page.calendly-description",
            defaultMessage:
              "Let the AI Agent send booking links and help customers schedule calls or consultations without leaving the conversation by using with Calendly",
          }),
          icon: <CalendlyIcon />,
          isConnected: calendlyTile.isConnected,
          needsReconnect: calendlyTile.needsSetup && !calcomTile.isConnected,
          isExpired: calendlyTile.isExpired,
          onConnect: () =>
            guardConnect(() => startOAuth("calendly", "Calendly")),
          onDisconnect: () =>
            handleDisconnect("Calendly", "calendly", calendlyTile.connectionId),
          onReconnect: () => openPicker("calendly", calendlyTile.connectionId),
          pendingLabel,
          pendingTooltip,
          onHowItWorks: () => setHowItWorksKind("calendly"),
          isConnectDisabled: isCalendlyConnectDisabled,
          connectDisabledTooltip: isCalendlyConnectDisabled
            ? schedulingAutomationConnectDisabledTooltip
            : undefined,
        },
        {
          ...baseTile,
          id: "shopify",
          title: t({
            id: "cx-actions-page.shopify",
            defaultMessage: "Shopify",
          }),
          description: t({
            id: "cx-actions-page.shopify-description",
            defaultMessage:
              "Connect your Shopify store to let the AI Agent answer product questions, check order status, and help customers place orders automatically",
          }),
          icon: <ShopifyIcon />,
          isConnected: isShopifyConnected,
          needsReconnect: false,
          isExpired: false,
          onConnect: () =>
            guardConnect(() => setIsShopifyStorePromptOpen(true)),
          onDisconnect: () => {
            const shopifyConn = connectionsByType.shopify?.[0];
            if (shopifyConn)
              handleDisconnect("Shopify", "shopify", shopifyConn.id);
          },
          onReconnect: () => {},
          onHowItWorks: () => setHowItWorksKind("shopify"),
          isConnectDisabled: isShopifyConnectDisabled,
          connectDisabledTooltip: isShopifyConnectDisabled
            ? shoppingAutomationConnectDisabledTooltip
            : undefined,
        },
        {
          ...baseTile,
          id: "square",
          title: t({
            id: "cx-actions-page.square",
            defaultMessage: "Square",
          }),
          description: t({
            id: "cx-actions-page.square-description",
            defaultMessage:
              "Integrate Square to manage payments and transactions while assisting customers with purchases and order information",
          }),
          icon: <SquareIcon />,
          isConnected: squareupTile.isConnected,
          needsReconnect: squareupTile.needsSetup && !isShopifyConnected,
          isExpired: squareupTile.isExpired,
          onConnect: () =>
            guardConnect(() => startOAuth("squareup", "Squareup")),
          onDisconnect: () =>
            handleDisconnect("Square", "squareup", squareupTile.connectionId),
          onReconnect: () => openPicker("squareup", squareupTile.connectionId),
          pendingLabel,
          pendingTooltip,
          onHowItWorks: () => setHowItWorksKind("squareup"),
          isConnectDisabled: isSquareConnectDisabled,
          connectDisabledTooltip: isSquareConnectDisabled
            ? shoppingAutomationConnectDisabledTooltip
            : undefined,
        },
        {
          ...baseTile,
          id: "cal",
          title: t({
            id: "cx-actions-page.calcom",
            defaultMessage: "Cal.com",
          }),
          description: t({
            id: "cx-actions-page.calcom-description",
            defaultMessage:
              "Use Cal.com to allow customers to book meetings directly through chat. The AI Agent can share available times and schedule appointments instantly",
          }),
          icon: <CalcomIcon />,
          isConnected: calcomTile.isConnected,
          needsReconnect: calcomTile.needsSetup && !calendlyTile.isConnected,
          isExpired: calcomTile.isExpired,
          onConnect: () => guardConnect(() => startOAuth("cal", "Cal")),
          onDisconnect: () =>
            handleDisconnect("Cal", "cal", calcomTile.connectionId),
          onReconnect: () => openPicker("cal", calcomTile.connectionId),
          pendingLabel,
          pendingTooltip,
          onHowItWorks: () => setHowItWorksKind("cal"),
          isConnectDisabled: isCalcomConnectDisabled,
          connectDisabledTooltip: isCalcomConnectDisabled
            ? schedulingAutomationConnectDisabledTooltip
            : undefined,
        },
      ];
    }, [
      t,
      calendlyTile.isConnected,
      calendlyTile.needsSetup,
      calendlyTile.isExpired,
      calendlyTile.connectionId,
      calcomTile.isConnected,
      calcomTile.needsSetup,
      calcomTile.isExpired,
      calcomTile.connectionId,
      squareupTile.isConnected,
      squareupTile.needsSetup,
      squareupTile.isExpired,
      squareupTile.connectionId,
      pendingLabel,
      pendingTooltip,
      shoppingAutomationConnectDisabledTooltip,
      schedulingAutomationConnectDisabledTooltip,
      isShopifyConnected,
      startOAuth,
      guardConnect,
      handleDisconnect,
      openPicker,
      connectionsByType.shopify,
    ]);

  return (
    <>
      <div className="flex flex-wrap gap-8">
        {integrations.map((integration) => (
          <CXIntegrationTile key={integration.id} {...integration} />
        ))}
      </div>
      {picker && (
        <ConnectionPickerDialog
          isOpen
          onClose={closePicker}
          provider={picker.provider}
          entityId={entityId}
          connectionId={picker.connectionId}
        />
      )}
      <IntegrationsHowItWorksDialog
        isOpen={howItWorksKind !== null}
        kind={howItWorksKind}
        onRequestClose={() => setHowItWorksKind(null)}
      />
      <ShopifyStorePromptDialog
        isOpen={isShopifyStorePromptOpen}
        onClose={() => setIsShopifyStorePromptOpen(false)}
        onSubmit={redirectToShopifyAuthorize}
      />
    </>
  );
};
